import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { contractTimelineAPI } from '@/lib/api';
import { CheckCircle2, AlertCircle, Heart } from 'lucide-react';

const COMPONENT_LABELS = {
  cert_complete: 'Certifications',
  invitation_complete: 'Invitation',
  no_overdue_followups: 'No Overdue',
  status_active: 'Status Healthy',
  carrier_responsive: 'Carrier Responsive',
};

export function ContractHealthScore({ requestId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!requestId) return;
    contractTimelineAPI.healthScore(requestId)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [requestId]);

  if (loading || !data) return null;

  const score = data.score;
  const tone =
    score >= 80 ? { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', label: 'Healthy' } :
    score >= 50 ? { bg: 'bg-amber-50',   ring: 'ring-amber-200',   text: 'text-amber-700',   label: 'At Risk' } :
                  { bg: 'bg-red-50',     ring: 'ring-red-200',     text: 'text-red-700',     label: 'Critical' };

  return (
    <Card className={`${tone.bg} ${tone.ring} ring-1 border-0`} data-testid="health-score-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={`flex flex-col items-center justify-center h-20 w-20 rounded-full bg-white ${tone.ring} ring-2 shrink-0`}>
            <Heart size={14} className={tone.text} />
            <p className={`text-2xl font-heading font-bold ${tone.text} leading-tight`}>{score}</p>
            <p className="text-[9px] uppercase tracking-wider text-slate-400">/ 100</p>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <p className="font-heading font-semibold text-slate-900">Contract Health</p>
              <span className={`text-xs font-medium ${tone.text}`}>{tone.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(data.components).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5 text-[11px]">
                  {val >= 1.0 ? (
                    <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                  ) : (
                    <AlertCircle size={12} className="text-red-500 shrink-0" />
                  )}
                  <span className="text-slate-600">{COMPONENT_LABELS[key] || key}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ContractHealthScore;
